/**
 * Maison shipping insurance — ON by default.
 *
 * Insurance is auto-added the moment the cart holds a real product, and
 * auto-removed once none remain (so the cart is never left holding only
 * insurance).
 *
 * Opt-out is sticky for the session: as soon as the shopper switches the toggle
 * OFF we stop auto-adding, so adding more products never turns it back on.
 * Switching it back ON clears the opt-out.
 *
 * This lives in a layout-loaded asset (not inside the cart drawer markup) so the
 * listener is always bound on page load — the cart drawer re-renders via
 * innerHTML, which would never execute an inline <script>, so the first
 * add-to-cart used to be missed. Reading the config from a global set by the
 * cart-drawer section keeps the variant id available without a section context.
 */
(function () {
  var OPT_OUT_KEY = 'maisonInsuranceOptedOut';
  var busy = false;

  function variantId() {
    // Set by the cart-drawer section; may be 0/undefined when no insurance product is configured.
    return Number(window.MAISON_INSURANCE_VARIANT_ID || 0);
  }

  function optedOut() {
    try { return sessionStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }

  function setOptedOut(value) {
    try { value ? sessionStorage.setItem(OPT_OUT_KEY, '1') : sessionStorage.removeItem(OPT_OUT_KEY); } catch (e) {}
  }

  // Throw on a failed cart request so callers can catch it.
  function ok(res) {
    if (!res.ok) throw new Error('cart request failed: ' + res.status);
    return res;
  }

  // Ask the drawer to re-fetch its full section HTML from the server. Deferred so
  // it runs AFTER any pending render the theme scheduled from its own cart:change
  // (its _onCartChanged uses a setTimeout that would otherwise clobber our result
  // against an already-replaced DOM and throw). Our refresh is the last write.
  function refreshDrawer() {
    setTimeout(function () {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }, 0);
  }

  // Add insurance. add.js creates the line (change.js can't add a missing line).
  function addInsurance() {
    var id = variantId();
    if (!id) return;
    busy = true;
    return fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: id, quantity: 1 })
    })
      .then(ok)
      .then(refreshDrawer)
      .catch(function () {})
      .finally(function () { busy = false; });
  }

  // Remove insurance by its line-item KEY (matching the theme's own line-item
  // removal). Removing by variant id is what returned a 400, so we look the line up
  // in the current cart and delete it by key.
  //
  // If insurance is the ONLY thing left, clear the whole cart instead: that yields
  // a clean empty state in one request and sidesteps the theme's _onCartChanged
  // race (it schedules a delayed innerHTML write against the pre-clear DOM, which
  // throws once our refresh has replaced it).
  function removeInsurance() {
    var id = variantId();
    if (!id) return;
    busy = true;
    return fetch(Shopify.routes.root + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var line = items.filter(function (item) {
          return item.variant_id === id || item.id === id;
        })[0];
        if (!line) return; // already gone

        var realCount = 0;
        items.forEach(function (item) {
          if (item.variant_id !== id && item.id !== id && (!item.properties || item.properties._maison_bfcm_gift !== '1')) realCount += item.quantity;
        });

        if (realCount === 0 && !items.some(function (item) { return item.properties && item.properties._maison_bfcm_gift === '1'; })) {
          return fetch(Shopify.routes.root + 'cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          }).then(ok).then(refreshDrawer);
        }

        return fetch(Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        }).then(ok).then(refreshDrawer);
      })
      .catch(function () {})
      .finally(function () { busy = false; });
  }

  function reconcileInsurance(event) {
    var id = variantId();
    if (!id) return;

    var cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;

    var hasInsurance = false;
    var realCount = 0;
    cart.items.forEach(function (item) {
      if (item.variant_id === id || item.id === id) {
        hasInsurance = true;
      } else if (!item.properties || item.properties._maison_bfcm_gift !== '1') {
        realCount += item.quantity;
      }
    });

    // Ignore events while our own add/remove request is in flight, to avoid loops.
    // (The manual toggle sets the opt-out itself and refreshes the drawer without a
    // cart:change, so it never reaches this listener.)
    if (busy) return;

    // Never leave a cart holding only insurance; clear the opt-out so the next
    // fresh cart defaults to ON again.
    if (hasInsurance && realCount === 0) {
      setOptedOut(false);
      removeInsurance();
      return;
    }

    // Default ON: add insurance whenever products exist and the shopper hasn't
    // opted out this session.
    if (!hasInsurance && realCount >= 1 && !optedOut()) {
      addInsurance();
    }
  }

  document.addEventListener('cart:change', reconcileInsurance);
  document.addEventListener('maison:bfcm-gift:change', reconcileInsurance);

  // ---------------------------------------------------------------------------
  // Checkout button loader — show the pill loader while the cart form submits.
  // Bound once on the document so it survives cart drawer re-renders.
  // ---------------------------------------------------------------------------
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('.maison-cart-footer__form');
    if (!form) return;
    var button = form.querySelector('.maison-checkout-button');
    var loader = button && button.querySelector('pill-loader');
    if (button && loader) {
      button.setAttribute('aria-busy', 'true');
      loader.setAttribute('aria-busy', 'true');
    }
  });

  // ---------------------------------------------------------------------------
  // <maison-insurance-toggle> — the manual insurance toggle in the cart footer.
  // Defined from this external asset so it upgrades reliably whenever the drawer
  // re-renders its markup (an inline <script> inside the drawer never executes).
  // On click it sets the sticky opt-out from the shopper's intent, mutates the
  // cart, then fires cart:refresh so the drawer re-renders from the server.
  // ---------------------------------------------------------------------------
  if (!window.customElements.get('maison-insurance-toggle')) {
    window.customElements.define('maison-insurance-toggle', class extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.maison-toggle');
        if (!this.toggle || this._bound) return;
        this._bound = true;
        this.toggle.addEventListener('click', this._onClick.bind(this));
      }

      get variantId() {
        return this.getAttribute('data-variant-id');
      }

      get lineKey() {
        return this.getAttribute('data-line-key');
      }

      get inCart() {
        return this.getAttribute('data-in-cart') === 'true';
      }

      async _onClick() {
        if (this.toggle.getAttribute('aria-busy') === 'true') return;
        if (!this.variantId) return;
        var turningOn = !this.inCart;
        this.toggle.setAttribute('aria-busy', 'true');
        // Reflect the intended state immediately for responsiveness.
        this.toggle.setAttribute('aria-checked', turningOn ? 'true' : 'false');

        // Record the sticky opt-out from the shopper's intent: OFF opts out for the
        // session, ON opts back in. Done here (not from a cart event) so it stays
        // reliable regardless of how the drawer re-renders.
        setOptedOut(!turningOn);

        // Reuse the same robust add/remove used by the auto logic (remove looks the
        // line up by key to avoid the change.js-by-variant-id 400).
        try {
          await (turningOn ? addInsurance() : removeInsurance());
        } catch (e) {
          this.toggle.removeAttribute('aria-busy');
          this.toggle.setAttribute('aria-checked', this.inCart ? 'true' : 'false');
        }
      }
    });
  }
})();
